/**
 * Dev-only auto-login for Playwright / local testing.
 *
 * Security: ONLY works when hostname is localhost or 127.0.0.1.
 * The actual authentication happens server-side via the Vite dev-auth plugin
 * which uses SUPABASE_SERVICE_KEY (never exposed to the browser).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

const DEV_HOSTS = new Set(['localhost', '127.0.0.1']);
type DevLoginProfile = 'admin' | 'consultant';

function getDevLoginProfile(): DevLoginProfile | null {
  if (typeof window === 'undefined') return null;
  if (!DEV_HOSTS.has(window.location.hostname)) return null;
  const value = new URLSearchParams(window.location.search).get('dev-login');
  if (value === 'true' || value === 'admin') return 'admin';
  if (value === 'consultant' || value === 'konsulent') return 'consultant';
  return null;
}

/** Check if the current page request is a dev-login attempt on localhost. */
export function isDevLoginEnabled(): boolean {
  return getDevLoginProfile() !== null;
}

/** Remove the dev-login query param from the URL without a page reload. */
export function cleanDevLoginUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('dev-login');
  window.history.replaceState({}, '', url.toString());
}

/**
 * Call the Vite dev-auth server endpoint and use the returned magic-link
 * token to create a real Supabase session.
 *
 * The endpoint is always at the server root (not affected by Vite `base`).
 */
export async function performDevLogin(supabase: SupabaseClient): Promise<void> {
  const profile = getDevLoginProfile();
  if (!profile) throw new Error('Ugyldig lokal QA-loginprofil.');
  const response = await fetch(`/__dev-auth?as=${encodeURIComponent(profile)}`);

  if (!response.ok) {
    let message = `Dev auth endpoint returned ${response.status}`;
    try {
      const body = await response.json();
      if (body.error) message = body.error;
    } catch { /* ignore parse errors */ }
    throw new Error(message);
  }

  const { token_hash, type } = await response.json();

  const { error } = await supabase.auth.verifyOtp({ token_hash, type });
  if (error) throw error;
}
